import { hmac } from '@noble/hashes/hmac.js';
import { sha1 as hash_sha1 } from '@noble/hashes/legacy.js';
import { sha256 as hash_sha256, sha512 as hash_sha512 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';

import { InvalidTotpSecretError, UnknownTotpAlgorithmError } from './errors';

export enum TotpAlgorithm {
  unknown = 'unknown',
  sha1 = 'sha1',
  sha256 = 'sha256',
  sha512 = 'sha512',
}

export const DEFAULT_TOTP_ALGORITHM = TotpAlgorithm.sha1;
export const DEFAULT_TOTP_DIGITS = 6;
export const DEFAULT_TOTP_PERIOD = 30;

export function calculateTotp(
  secretBase32: string,
  algorithm: TotpAlgorithm,
  digits: number,
  period: number,
  now: number = Date.now(),
): {
  totp: string;
  epochStart: number;
  epochEnd: number;
  epochLeftMs: number;
} {
  const epoch = Math.round(now / 1000.0);
  const counter = Math.floor(epoch / period);
  const epochStart = counter * period * 1000;
  const epochEnd = (counter + 1) * period * 1000;
  const epochLeftMs = epochEnd - now;
  const totp = calculateHotp(secretBase32, algorithm, digits, counter);
  return { totp, epochStart, epochEnd, epochLeftMs };
}

function calculateHotp(
  secretBase32: string,
  algorithm: TotpAlgorithm,
  digits: number,
  counter: number,
) {
  const base32bytes = decodeBase32(secretBase32);
  const hexCounterBytes = hexToBytes(zeropad(counter.toString(16), 16));

  let hashAlgorithm: any;

  switch (algorithm) {
    case TotpAlgorithm.sha1:
      hashAlgorithm = hash_sha1;
      break;
    case TotpAlgorithm.sha256:
      hashAlgorithm = hash_sha256;
      break;
    case TotpAlgorithm.sha512:
      hashAlgorithm = hash_sha512;
      break;
    default:
      throw new UnknownTotpAlgorithmError();
  }

  const hmacHex = bytesToHex(hmac(hashAlgorithm, base32bytes, hexCounterBytes));

  const offset = parseInt(hmacHex.substring(hmacHex.length - 1), 16);
  const otpFull = // eslint-disable-next-line no-bitwise
    (parseInt(hmacHex.substring(offset * 2, offset * 2 + 8), 16) & 0x7fffffff).toString();
  const otp = otpFull.substring(otpFull.length - digits, otpFull.length);
  return zeropad(otp, digits);
}

/* License for decodeBase32
 *
 * The MIT License (MIT)
 *
 * Copyright © Héctor Molinero Fernández
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// based on https://github.com/hectorm/otpauth/blob/049ad5eae16c442c2cea8be00c15dc659ae9e2fa/src/internal/encoding/base32.js
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
export function decodeBase32(str: string): Uint8Array {
  // Remove spaces (although they are not allowed by the spec, some issuers add them for readability).
  str = str.replace(/ /g, '');

  // Canonicalize to all upper case and remove padding if it exists.
  let end = str.length;
  while (str[end - 1] === '=') --end;
  str = (end < str.length ? str.substring(0, end) : str).toUpperCase();

  // eslint-disable-next-line no-bitwise
  const buf = new ArrayBuffer(((str.length * 5) / 8) | 0);
  const arr = new Uint8Array(buf);
  let bits = 0;
  let value = 0;
  let index = 0;

  for (let i = 0; i < str.length; i++) {
    const idx = ALPHABET.indexOf(str[i]!);
    if (idx === -1) throw new InvalidTotpSecretError();
    if (index >= arr.length) {
      // If we're past the end of the array, drop the extra character. This is
      // not strictly correct, but b32 implementations in the wild seem to be
      // fail-slow so we'll follow suit.
      break;
    }

    // eslint-disable-next-line no-bitwise
    value = (value << 5) | idx;
    bits += 5;

    if (bits >= 8) {
      bits -= 8;
      // eslint-disable-next-line no-bitwise
      arr[index++] = value >>> bits;
    }
  }

  return arr;
}

function zeropad(str: string, len: number): string {
  if (len <= str.length) {
    return str;
  }
  return Array(len + 1 - str.length).join('0') + str;
}

export function shouldShowTotpTimeOffsetWarning(clientServerTimeOffsetMs: number | null) {
  return (
    clientServerTimeOffsetMs !== null &&
    Math.abs(clientServerTimeOffsetMs) > DEFAULT_TOTP_PERIOD * 1000
  );
}
